import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Lock, Globe2, BadgeCheck, FileLock } from "lucide-react";

const standards = [
  {
    icon: ShieldCheck,
    code: "HIPAA",
    region: "United States",
    desc: "Protected Health Information safeguards under U.S. federal law.",
  },
  {
    icon: FileLock,
    code: "DISHA",
    region: "India",
    desc: "Digital Information Security in Healthcare Act — patient data integrity.",
  },
  {
    icon: Globe2,
    code: "GDPR",
    region: "European Union",
    desc: "General Data Protection Regulation — user consent and data sovereignty.",
  },
  {
    icon: BadgeCheck,
    code: "NABH",
    region: "India",
    desc: "National Accreditation Board standards for clinical quality and safety.",
  },
  {
    icon: Lock,
    code: "DPDP",
    region: "India",
    desc: "Digital Personal Data Protection Act — lawful, fair data processing.",
  },
];

export const Compliance = () => {
  return (
    <section
      className="relative py-20 md:py-24 bg-sentivo-bg border-t border-sentivo-line"
      data-testid="compliance-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="grid lg:grid-cols-12 gap-10 mb-12 items-end"
        >
          <div className="lg:col-span-7">
            <div className="text-xs tracking-[0.24em] uppercase text-sentivo-secondary mb-5">
              Trust & Compliance
            </div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-sentivo-ink text-balance">
              Built to the highest
              <span className="italic text-sentivo-accent"> health, privacy </span>
              and safety standards.
            </h2>
          </div>
          <p className="lg:col-span-5 text-base md:text-lg text-sentivo-muted leading-relaxed">
            Our systems are designed and audited against globally recognised health
            and data‑protection frameworks — so families, clinicians and partners can
            trust every signal we surface.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5"
        >
          {standards.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.code}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                whileHover={{ y: -3 }}
                className="group relative rounded-2xl border border-sentivo-line bg-white p-5 md:p-6 transition-shadow hover:shadow-md"
                data-testid={`compliance-${s.code.toLowerCase()}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-sentivo-surface text-sentivo-accent">
                    <Icon size={16} strokeWidth={1.7} />
                  </span>
                  <div className="font-serif text-lg md:text-xl text-sentivo-ink leading-none">
                    {s.code}
                  </div>
                </div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-sentivo-secondary mb-2">
                  {s.region}
                </div>
                <p className="text-xs text-sentivo-muted leading-relaxed">{s.desc}</p>
              </motion.div>
            );
          })}
        </motion.div>

        <div className="mt-10 text-xs text-sentivo-muted/80 max-w-3xl">
          Compliance posture is continuously reviewed. Specific certifications may
          apply per product, deployment region and customer engagement. Contact us
          for our latest compliance brief.
        </div>
      </div>
    </section>
  );
};
